import { useContext, useEffect, useState, useRef } from "react";

//Components
import CheckboxWithLabel from "./CheckboxWithLabel";
import SingleDropDown from "./SingleDropDown";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import SearchModal from "./SearchModal";
import SearchButtons from "./SearchButtons";

//Fetching
import { useLazyQuery } from "@apollo/client";
import { useMutation } from "@apollo/client";

//Contexts
import { SetupContext } from "../Contexts/SetupContext";
import { UserContext } from "../Contexts/UserContext";

//styling
import { withStyles } from "@material-ui/core/styles";
import { useStyles } from "../Styles/styles";

//utils
import { PEOPLE_SEARCH_QUERY } from "../Queries/PeopleSearchQuery";
import { parsePeopleSearch, peopleSearchHeaders } from "../utils/SearchHelpers";
import HelpIconButton from "./HelpIconButton";
import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../Mutations/AddOrUpdatePersonInIncident";

import moment from "moment";

const initialSearchState = {
  personal_attribute_attributes: {
    organization: "0",
    last_name: "",
    first_name: "",
    dob_search_start_date: "",
    juvenile: "0",
    drivers_license: "",
    license_state_id: null,
    phone: "",
    home_address_attributes: {
      street_number_start: "",
      street_number_end: "",
      street_name: "",
      city: "",
    },
  },
};
const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: props =>
      props.nightmode === "true" ? "#616161" : "#fae8ad",
    // backgroundColor: '#fae8ad',
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      // margin: '12px 0',
      margin: "0",
    },
  },
  expanded: {},
})(AccordionSummary);

const PersonSearch = ({
  handlePersonOpening,
  showModal,
  setShowModal,
  incidentId = null,
  handleAddPersonMatchToIncidentState,
  incidentPeople = []
}) => {
  //When adding the ability to add a person to an incident use the memoizedLocked
  const classes = useStyles();
  const { user, responseErrorHandler, nightMode } = useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [searchResults, setSearchResults] = useState(null);
  const [searchState, setSearchState] = useState(initialSearchState);

  const { states } = setup.setupLists || {};

  const [
    searchPeople,
    { loading: peopleSearchLoading, error: peopleSearchError },
  ] = useLazyQuery(PEOPLE_SEARCH_QUERY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setSearchResults(data?.searchPeople);
        setShowSearchOptions(false);
      }
    },
  });

  const [addPersonToIncident] = useMutation(ADD_OR_UPDATE_PERSON_IN_INCIDENT, {
    onCompleted: data => {
      if (data) {
        !!handleAddPersonMatchToIncidentState &&
          handleAddPersonMatchToIncidentState(
            data.addOrUpdatePersonInIncident.incidentUuid,
            data.addOrUpdatePersonInIncident.personIncidentSpecific,
          );
        setShowModal(false);
      }
    },
  });

  const handleAddPersonToIncident = (person, incidentId) => {
    if (!incidentId || !person) {
      return;
    }
    const personAlreadyOnIncident = incidentPeople.some(
      p => p.id === person?.id,
    );
    if (personAlreadyOnIncident) {
      alert("This person is already on the incident");
      return;
    }
    addPersonToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId,
          personId: person.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const handlePersonalAttributeChange = (value, name) => {
    setSearchState(prevState => {
      return {
        ...prevState,
        personal_attribute_attributes: {
          ...prevState.personal_attribute_attributes,
          [name]: value,
        },
      };
    });
    return null;
  };

  useEffect(() => {
    if (searchResults?.length < 0) {
      setSearchResults([]);
    }
  }, [searchState, resultsPerPage, searchResults?.length]);

  const handleAddressChange = (value, name) => {
    setSearchState(prevState => ({
      ...prevState,
      personal_attribute_attributes: {
        ...prevState.personal_attribute_attributes,
        home_address_attributes: {
          ...prevState.personal_attribute_attributes.home_address_attributes,
          [name]: value,
        },
      },
    }));
    return null;
  };

  const submitSearch = (_perPage = resultsPerPage, page = 1) => {
    const searchStr = JSON.stringify(searchState);
    searchPeople({
      variables: { perPage: resultsPerPage, page: page, json: searchStr },
    });
  };

  const dashRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: dashRef?.current?.offsetWidth,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      dashRef.current &&
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDashSize({
      width: dashRef?.current?.offsetWidth,
      height: dashRef?.current?.offsetHeight,
    });
  }, [dashRef, showModal]);

  const [showSearchOptions, setShowSearchOptions] = useState(true);

  return showModal ? (
    <SearchModal showModal={showModal} setShowModal={setShowModal}>
      <form
        className={classes.paper}
        ref={dashRef}
        style={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        <Accordion
          nightMode={nightMode}
          square
          expanded={showSearchOptions}
          type="button"
          onChange={() => setShowSearchOptions(!showSearchOptions)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            style={{ margin: 0 }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={classes.expansionPanelTypography}
                data-cy="initial-call-information-panel-header"
              >
                Person Search{" "}
                {showSearchOptions ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Typography>
              <HelpIconButton objectname="mobile_person_search" />
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails type="button" onClick={e => e.stopPropagation()}>
            <div
              style={{ display: "flex", flexDirection: "column", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  height: "auto",
                  justifyContent: "flex-start",
                }}
              >
                <CheckboxWithLabel
                  checked={
                    searchState?.personal_attribute_attributes?.organization ===
                    "1"
                      ? true
                      : false
                  }
                  stateName={"organization"}
                  onChange={e =>
                    handlePersonalAttributeChange(
                      e.target.checked === true ? "1" : "0",
                      "organization",
                    )
                  }
                  title={"Bus."}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <TextField
                  data-cy="last-name"
                  name={"last_name"}
                  inputProps={{ "data-cy": "last-name-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="last-name-id"
                  label="Last Name"
                  value={searchState?.personal_attribute_attributes?.last_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handlePersonalAttributeChange(e.target.value, "last_name")
                  }
                />
                <TextField
                  data-cy="first-name"
                  name={"first_name"}
                  inputProps={{ "data-cy": "first-name-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="first-name-id"
                  label="First Name"
                  value={searchState?.personal_attribute_attributes?.first_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handlePersonalAttributeChange(e.target.value, "first_name")
                  }
                />
                <FormControl variant="standard" className={classes.formControl}>
                  <KeyboardDatePicker
                    data-cy="dob"
                    clearable
                    showTodayButton
                    disableFuture
                    maxDateMessage={"Date cannot be in the future"}
                    label="DOB"
                    value={
                      searchState?.personal_attribute_attributes
                        ?.dob_search_start_date
                        ? moment.utc(
                            searchState?.personal_attribute_attributes
                              ?.dob_search_start_date,
                            "MM-DD-YYYY",
                          )
                        : null
                    }
                    onChange={(dateObj) =>
                      handlePersonalAttributeChange(
                        dateObj?.format("MM/DD/YYYY") || "",
                        "dob_search_start_date",
                      )
                    }
                    format="MM/DD/YYYY"
                    InputProps={{ style: { borderRadius: "5px" } }}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: "100%" }}
                  />
                </FormControl>
                <CheckboxWithLabel
                  checked={
                    searchState?.personal_attribute_attributes?.juvenile === "1"
                      ? true
                      : false
                  }
                  stateName={"juvenile"}
                  onChange={e =>
                    handlePersonalAttributeChange(
                      e.target.checked === true ? "1" : "0",
                      "juvenile",
                    )
                  }
                  title={"Juvenile"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <TextField
                  data-cy="drivers-license"
                  name={"drivers_license"}
                  inputProps={{ "data-cy": "drivers-license-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="drivers-license-id"
                  label="Driver's License"
                  value={
                    searchState?.personal_attribute_attributes?.drivers_license
                  }
                  className={classes.matchMargin}
                  onChange={e =>
                    handlePersonalAttributeChange(
                      e.target.value,
                      "drivers_license",
                    )
                  }
                />
                <SingleDropDown
                  handleChange={e =>
                    handlePersonalAttributeChange(
                      e.target.value?.id?.toString(),
                      "license_state_id",
                    )
                  }
                  stateName={"license_state_id"}
                  value={states?.find(
                    state =>
                      state.id ===
                      searchState?.personal_attribute_attributes
                        ?.license_state_id,
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={states}
                  title={"State"}
                  // bgColor={"white"}
                  bRadius={"5px"}
                  classStyle={classes.dropdown}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <TextField
                  data-cy="primary-phone"
                  name={"phone"}
                  inputProps={{ "data-cy": "primary-phone-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="primary-phone-id"
                  label="Primary Phone"
                  value={searchState?.personal_attribute_attributes?.phone}
                  className={classes.matchMargin}
                  onChange={e =>
                    handlePersonalAttributeChange(e.target.value, "phone")
                  }
                />

                {/* <TextField
          data-cy="street-number"
          name={'street_number'}
          inputProps={{ "data-cy": "street-number-input" }}
          InputProps={{ style: {  borderRadius: '5px' } }}
          InputLabelProps={{ shrink: true }}
          id="street-number-id"
          label="Street Number"
          value={searchState?.personal_attribute_attributes?.address_attributes?.street_number}
          className={classes.matchMargin}
          onChange={(e) => handleAddressChange(e.target.value, "street_number")}
        /> */}
                <TextField
                  data-cy="street-number-start"
                  name={"street_number_start"}
                  inputProps={{ "data-cy": "street-number-start-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="street-number-start-id"
                  label="Street # Start"
                  value={
                    searchState?.personal_attribute_attributes
                      ?.home_address_attributes?.street_number_start
                  }
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAddressChange(e.target.value, "street_number_start")
                  }
                />
                <TextField
                  data-cy="street-number-end"
                  name={"street_number_end"}
                  inputProps={{ "data-cy": "street-number-end-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="street-number-end-id"
                  label="Street # End"
                  value={
                    searchState?.personal_attribute_attributes
                      ?.home_address_attributes?.street_number_end
                  }
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAddressChange(e.target.value, "street_number_end")
                  }
                />
                <TextField
                  data-cy="street-name"
                  name={"street_name"}
                  inputProps={{ "data-cy": "street-name-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="street-name-id"
                  label="Street Name"
                  value={
                    searchState?.personal_attribute_attributes
                      ?.home_address_attributes?.street_name
                  }
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAddressChange(e.target.value, "street_name")
                  }
                />
                <TextField
                  data-cy="city"
                  name={"city"}
                  inputProps={{ "data-cy": "city-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="city-id"
                  label="City"
                  value={
                    searchState?.personal_attribute_attributes
                      ?.home_address_attributes?.city
                  }
                  className={classes.matchMargin}
                  onChange={e => handleAddressChange(e.target.value, "city")}
                />

                <div className={classes.matchMargin}>
                  <InputLabel
                    shrink={true}
                    className={classes.label}
                    data-cy={"results-per-page-label"}
                    ref={inputLabel}
                    id={"results-per-page-label"}
                  >
                    Results Per Page
                  </InputLabel>
                  <Select
                    data-cy={"results-per-page-select"}
                    id={"results-per-page-select"}
                    name={"resultPerPage"}
                    value={resultsPerPage}
                    native={false}
                    // renderValue={() => !nameFormatter ? (value?.[name] ? value[name] : " ") : nameFormatter(value)}
                    onChange={e => setResultsPerPage(e.target.value)}
                    style={{ marginTop: "0px", borderRadius: "5px" }}
                  >
                    <MenuItem
                      data-cy={"10-results-per-page"}
                      key={10}
                      value={10}
                    >
                      10
                    </MenuItem>
                    <MenuItem
                      data-cy={"25-results-per-page"}
                      key={25}
                      value={25}
                    >
                      25
                    </MenuItem>
                    <MenuItem
                      data-cy={"50-results-per-page"}
                      key={50}
                      value={50}
                    >
                      50
                    </MenuItem>
                  </Select>
                </div>
              </div>
              <SearchButtons
                loading={peopleSearchLoading}
                submitSearch={submitSearch}
                setSearchResults={setSearchResults}
                setShowModal={setShowModal}
                resetSearchState={() => setSearchState(initialSearchState)}
                clearSearchResults={() => setSearchResults(null)}
              />
            </div>
          </AccordionDetails>
        </Accordion>
        {!!searchResults && (
          <PaginatedTableWrapper
            dashSize={dashSize}
            tableHeight={dashRef?.current?.offsetHeight * 0.95}
            tableName={"People Search Results"}
            headers={peopleSearchHeaders(incidentId)}
            userId={user?.id}
            paginatedQueryHandler={submitSearch}
            rows={
              searchResults
                ? parsePeopleSearch(
                    searchResults,
                    incidentId,
                    handlePersonOpening,
                    handleAddPersonToIncident,
                    setShowModal,
                  )
                : []
            }
            loading={peopleSearchLoading}
            error={peopleSearchError}
            paginated={true}
            rowsPerPage={resultsPerPage}
          />
        )}
      </form>
    </SearchModal>
  ) : null;
};

export default PersonSearch;
